import { z } from 'zod';
import {
  aggregateKind,
  isSplitMetric,
  listAggregateKinds,
  type ListAggregateKind,
  type ListAggregateRelation,
  type ListColumn,
  type ListDefinition,
  type ListMetric,
  type ListSimpleAggregate,
  type ListSingleMetric,
} from './contract.ts';

/**
 * Kennzahl-Kopf (FR-95, AD-35, Story 1.32): Kacheln einer Ansicht über der Tabelle, immer zum aktuellen Filter. Eine
 * Kachel ist die Zahl der Zeilen oder ein vom Besitzer an der Spalte angemeldetes Aggregat (`ListColumn.aggregate`).
 * Rein (ohne Datenbank): Schema, Prüfung gegen das Register und Beschriftung; die Abfrage baut `reporting`.
 */

/** Höchstzahl Kacheln je Ansicht (EXPERIENCE: 2–6 Kacheln; 0 = kein Kennzahl-Kopf). */
export const LIST_METRICS_MAX = 6;

export const METRIC_TEXTS = {
  tooMany: `Höchstens ${LIST_METRICS_MAX} Kennzahlen je Ansicht.`,
  duplicate: 'Diese Kennzahl ist doppelt gewählt.',
  unknown: 'Diese Kennzahl kennt das ERP nicht (Zahl der Zeilen oder eine an der Spalte angebotene Kennzahl).',
  columnUnknown: (column: string) => `Kennzahl: Spalte „${column}“ gibt es in dieser Liste nicht.`,
  notOffered: (label: string) => `Diese Kennzahl bietet die Spalte „${label}“ nicht an.`,
  suspendedColumn: (column: string) => `Kennzahl ausgesetzt: Spalte „${column}“ gibt es nicht mehr.`,
  suspendedAggregate: (label: string) => `Kennzahl ausgesetzt: „${label}“ bietet diese Kennzahl nicht mehr an.`,
} as const;

const columnAggregates = listAggregateKinds.filter((k): k is Exclude<ListAggregateKind, 'count'> => k !== 'count');

// Jeder Fehler (unbekannte Art, fremdes Feld, fehlende Spalte) ergibt denselben deutschen Satz an der Kachel.
const unknownMetric = { error: METRIC_TEXTS.unknown };
/** Höchstlänge einer eigenen Beschriftung (Kachel, Teilwert, Aufteilung). */
export const LIST_METRIC_LABEL_MAX = 60;
/** Teilwerte einer Aufteilung (Story 4.23: bis zu 3 nebeneinander). */
export const LIST_SPLIT_PARTS_MIN = 2;
export const LIST_SPLIT_PARTS_MAX = 3;
const metricLabelSchema = z.string(unknownMetric).trim().min(1, METRIC_TEXTS.unknown).max(LIST_METRIC_LABEL_MAX, METRIC_TEXTS.unknown);
export const listSingleMetricSchema = z.union(
  [
    z.strictObject({ aggregate: z.literal('count', unknownMetric), label: metricLabelSchema.optional() }, unknownMetric),
    z.strictObject(
      {
        column: z.string(unknownMetric).min(1, METRIC_TEXTS.unknown).max(120, METRIC_TEXTS.unknown),
        aggregate: z.enum(columnAggregates as [Exclude<ListAggregateKind, 'count'>, ...Exclude<ListAggregateKind, 'count'>[]], unknownMetric),
        label: metricLabelSchema.optional(),
      },
      unknownMetric,
    ),
  ],
  unknownMetric,
);
export const listMetricSchema = z.union(
  [
    listSingleMetricSchema,
    z.strictObject(
      {
        split: metricLabelSchema,
        parts: z.array(listSingleMetricSchema, unknownMetric).min(LIST_SPLIT_PARTS_MIN, METRIC_TEXTS.unknown).max(LIST_SPLIT_PARTS_MAX, METRIC_TEXTS.unknown),
      },
      unknownMetric,
    ),
  ],
  unknownMetric,
);

/** Kacheln einer Ansicht in Anzeigereihenfolge (0–6; eine Aufteilung zählt als eine Kachel). */
export const listMetricsSchema = z.array(listMetricSchema).max(LIST_METRICS_MAX, METRIC_TEXTS.tooMany);

// Das Schema liefert genau den Vertragstyp.
const _metricFitsType: (m: z.output<typeof listMetricSchema>) => ListMetric = (m) => m;
void _metricFitsType;

/** Eine geprüfte Kennzahl mit ihrer Stelle (Kachel bzw. Teilwert einer Aufteilung, `splitPartIndex`). */
export type CheckedSingleMetric = { index: number; label?: string } & (
  | { kind: 'count' }
  | { kind: ListSimpleAggregate; column: ListColumn }
  | { kind: 'ratio'; column: ListColumn; numerator: ListColumn; denominator: ListColumn }
  | { kind: 'count_distinct'; column: ListColumn; relation: ListAggregateRelation }
);

/** Eine geprüfte Kachel mit ihrer Stelle in der Ansicht; eine Aufteilung mit ihren Teilwerten. */
export type CheckedMetric = CheckedSingleMetric | { index: number; kind: 'split'; label: string; parts: CheckedSingleMetric[] };

/**
 * Stelle des Teilwerts `part` der Aufteilung an Stelle `index` – eigene Nummern ab 100, damit die Abfragen je Teilwert
 * laufen (die Zeitgrenze gilt je Abfrage) und nie mit einer Kachel zusammenfallen.
 */
export function splitPartIndex(index: number, part: number): number {
  return 100 + index * 10 + part;
}

export interface SuspendedMetric {
  index: number;
  column: string;
  text: string;
}

export interface CheckedMetrics {
  metrics: CheckedMetric[];
  /** Ausgesetzte Kacheln einer gespeicherten Ansicht (Spalte entfallen oder Kennzahl nicht mehr angeboten). */
  suspended: SuspendedMetric[];
  /** Fehler je Feld (`metrics.0.column` → Satz); leer = gültig. */
  errors: Record<string, string>;
}

/** Schlüssel einer Kachel (Doppel erkennen); die eigene Beschriftung zählt nicht mit. */
export function metricKey(metric: ListMetric): string {
  if (isSplitMetric(metric)) return `split:${metric.split}`;
  return 'column' in metric ? `${metric.column}:${metric.aggregate}` : 'count';
}

type SingleCheck = { ok: CheckedSingleMetric } | { suspended: SuspendedMetric } | { error: [string, string] };

function checkSingle(
  columns: ReadonlyMap<string, ListColumn>,
  metric: ListSingleMetric,
  index: number,
  at: string,
  options: { unknown: 'suspend' | 'reject'; invalidAggregates?: 'suspend' | 'reject' },
): SingleCheck {
  const label = metric.label !== undefined ? { label: metric.label } : {};
  if (!('column' in metric)) return { ok: { index, kind: 'count', ...label } };
  const column = columns.get(metric.column);
  if (!column) {
    if (options.unknown === 'suspend') return { suspended: { index, column: metric.column, text: METRIC_TEXTS.suspendedColumn(metric.column) } };
    return { error: [`${at}.column`, METRIC_TEXTS.columnUnknown(metric.column)] };
  }
  const declared = column.aggregate?.find((a) => aggregateKind(a) === metric.aggregate);
  if (!declared) {
    if ((options.invalidAggregates ?? options.unknown) === 'suspend') return { suspended: { index, column: metric.column, text: METRIC_TEXTS.suspendedAggregate(column.label) } };
    return { error: [`${at}.aggregate`, METRIC_TEXTS.notOffered(column.label)] };
  }
  if (typeof declared === 'string') return { ok: { index, kind: declared, column, ...label } };
  if (declared.kind === 'ratio') {
    // Zähler und Nenner prüft das Register beim Start (Spalten derselben Liste).
    return { ok: { index, kind: 'ratio', column, numerator: columns.get(declared.numerator)!, denominator: columns.get(declared.denominator)!, ...label } };
  }
  return { ok: { index, kind: 'count_distinct', column, relation: declared.relation, ...label } };
}

/**
 * Prüft Kacheln gegen die Spalten einer Liste: Spalte aus dem Register, Aggregat an der Spalte angemeldet, ohne Doppel.
 * Gespeicherte Ansichten (`unknown: 'suspend'`): Kacheln auf entfallene Spalten werden ausgesetzt und gemeldet, nie
 * still entfernt; nicht (mehr) angebotene Aggregate ebenso, wenn `invalidAggregates: 'suspend'` (Lesen einer Ansicht),
 * sonst Fehler (Speichern, Abfrage). Eine Aufteilung wird ausgesetzt, sobald einer ihrer Teilwerte ausgesetzt ist.
 */
export function checkListMetrics(
  columns: ReadonlyMap<string, ListColumn>,
  metrics: readonly ListMetric[],
  options: { unknown: 'suspend' | 'reject'; invalidAggregates?: 'suspend' | 'reject' },
): CheckedMetrics {
  const out: CheckedMetric[] = [];
  const suspended: SuspendedMetric[] = [];
  const errors: Record<string, string> = {};
  if (metrics.length > LIST_METRICS_MAX) errors.metrics = METRIC_TEXTS.tooMany;
  const seen = new Set<string>();
  metrics.forEach((metric, index) => {
    const at = `metrics.${index}`;
    const key = metricKey(metric);
    if (seen.has(key)) {
      errors[at] = METRIC_TEXTS.duplicate;
      return;
    }
    seen.add(key);
    if (!isSplitMetric(metric)) {
      const result = checkSingle(columns, metric, index, at, options);
      if ('ok' in result) out.push(result.ok);
      else if ('suspended' in result) suspended.push(result.suspended);
      else errors[result.error[0]] = result.error[1];
      return;
    }
    const parts: CheckedSingleMetric[] = [];
    let partSuspended: SuspendedMetric | null = null;
    let failed = false;
    metric.parts.forEach((part, p) => {
      const result = checkSingle(columns, part, splitPartIndex(index, p), `${at}.parts.${p}`, options);
      if ('ok' in result) parts.push(result.ok);
      else if ('suspended' in result) partSuspended ??= { ...result.suspended, index };
      else {
        errors[result.error[0]] = result.error[1];
        failed = true;
      }
    });
    if (failed) return;
    if (partSuspended) suspended.push(partSuspended);
    else out.push({ index, kind: 'split', label: metric.split, parts });
  });
  return { metrics: out, suspended, errors };
}

/** Ist das Ergebnis der Kennzahl ein Anteil in Prozent (Quote Betrag ÷ Betrag, Story 4.23)? */
export function isShareRatio(metric: CheckedSingleMetric): boolean {
  return metric.kind === 'ratio' && metric.numerator.type === 'money' && metric.denominator.type === 'money';
}

/**
 * Anteile auf ganze Prozent (Story 4.23, Kachel „Aufteilung“): je Anteil abgerundet, der Rest bis zur gerundeten Summe
 * geht an den größten Anteil (bei Gleichstand an den ersten). Leere Anteile zählen als 0; sind alle leer, bleibt alles
 * leer (kein Teilen durch 0 – Anzeige „–“). Beispiel: 33,3 · 33,3 · 33,3 → 34 · 33 · 33; 60 · 15 · 25 bleibt.
 */
export function splitPercents(values: readonly (number | null)[]): (number | null)[] {
  if (values.every((v) => v === null)) return values.map(() => null);
  const raw = values.map((v) => v ?? 0);
  const floors = raw.map((v) => Math.floor(v + 1e-9));
  const target = Math.round(raw.reduce((a, b) => a + b, 0));
  const rest = target - floors.reduce((a, b) => a + b, 0);
  let largest = 0;
  raw.forEach((v, i) => {
    if (v > raw[largest]!) largest = i;
  });
  return floors.map((v, i) => (i === largest ? v + rest : v));
}

/**
 * Beschriftung einer Kachel, z. B. „Kunden“, „verfügbar“, „Ø VK netto“, „unter Mindestbestand“; eigene Beschriftung zuerst.
 * Summe ohne „Σ“ (Ticket 1.294, Sichtung HS53: Deko ohne Aussage) – nur Ø, min. und max. tragen ein Zeichen.
 */
export function metricLabel(definition: Pick<ListDefinition, 'rowNoun'>, metric: CheckedMetric): string {
  if (metric.label !== undefined) return metric.label;
  switch (metric.kind) {
    case 'count':
      return definition.rowNoun.many;
    case 'sum':
      return metric.column.label;
    case 'avg':
      return `Ø ${metric.column.label}`;
    case 'min':
      return `${metric.column.label} min.`;
    case 'max':
      return `${metric.column.label} max.`;
    case 'split':
      return metric.label;
    default:
      return metric.column.label;
  }
}
